import React, { useCallback, useEffect, useState } from 'react'

export function useInViewport<T extends HTMLElement>(): {
  isInViewport: boolean
  ref: React.RefCallback<T>
  scrollIntoView: () => void
} {
  const [isInViewport, setIsInViewport] = useState(false)
  const [refElement, setRefElement] = useState<T | null>(null)
  const [hasRun, setHasRun] = useState(false)

  const setRef = useCallback((node: T | null) => {
    if (node !== null) {
      setRefElement(node)
    }
  }, [])

  useEffect(() => {
    if (refElement && !isInViewport) {
      const observer = new IntersectionObserver(([entry]) => {
        setIsInViewport(entry?.isIntersecting || false)
        setHasRun(true)
      })
      observer.observe(refElement)

      return () => {
        observer.disconnect()
      }
    }
  }, [isInViewport, refElement])

  const scrollIntoView = useCallback(() => {
    if (!isInViewport && refElement && hasRun) {
      const scrollContainer = refElement.closest<HTMLElement>('.no-scrollbar')

      if (scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect()
        const elementRect = refElement.getBoundingClientRect()
        const delta =
          elementRect.left +
          elementRect.width / 2 -
          (containerRect.left + containerRect.width / 2)

        scrollContainer.scrollBy({ left: delta, behavior: 'smooth' })
      }
    }
  }, [isInViewport, refElement, hasRun])

  return { isInViewport, scrollIntoView, ref: setRef }
}
